// kPhoneNumberCore — normalise a written-down phone number to E.164 ("+46701234567").
//
// WHY THIS EXISTS. Any product that joins two address spaces on "the same person" needs ONE
// spelling of a phone number. WhatsApp hands out numbers already in international form (a JID's
// user part is E.164 without the plus); a human address book does not — the same contact is
// "070-123 45 67", "+46 70 123 45 67", "0046701234567" and "(070) 1234567" in four different
// records. Joining those without normalisation silently produces four people.
//
// SCOPE, STATED HONESTLY. This is a syntactic normaliser, not libphonenumber. It does NOT know
// national numbering plans, so it cannot tell a valid Swedish mobile from an invalid one, and it
// cannot split a country code out of a bare international string. What it does know is the four
// notations above, which is what address-book exports actually contain. Anything it cannot resolve
// WITHOUT GUESSING returns "" — deliberately, because a wrong join is worse than a missing one:
// a missing join shows up as an unnamed contact, a wrong join attributes one person's messages to
// another. Callers are expected to count and report the empties rather than discard them.
//
// No DOM, no I/O, no dependencies — safe in a browser, in Node, and in an Azure Function.

import { getCoreUtils, nz } from "./kCoreHandler";

export class kPhoneNumberCore {

    /**
     * Cut a reason to 80 characters, then mask ASCII digit runs of seven or more.
     * This is not a general secret/Unicode identifier scrubber; truncation precedes masking.
     * In error mode, or when a native fault occurs (reported once), the answer is "".
     */
    public static ReasonText = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(Why).substring(0, 80).replace(new RegExp("[0-9]{7,}", "g"), "***");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.ReasonText");
            return "";
        }
    };

    /** Everything a human writes into a phone field that carries no information. */
    private static SEPARATORS: RegExp = /[\s\-().\u00A0\u2010-\u2015\uFE0F]/g;

    /** E.164 allows at most 15 digits; fewer than 7 is not a dialable international number. */
    private static MIN_DIGITS: number = 7;
    private static MAX_DIGITS: number = 15;

    /**
     * Normalise `Raw` to E.164, or "" when that cannot be done without guessing.
     *
     * `DefaultCountryCode` is the country the LOCAL-format numbers in this source belong to,
     * digits only and without the plus ("46", "34", "62"). It is used for exactly one case — a
     * number written in national trunk form ("070...") — and ignored otherwise. Pass "" when the
     * source mixes countries and you would rather lose those rows than mis-assign them.
     */
    public static ToE164 = (Raw: string, DefaultCountryCode: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Raw === null || Raw === undefined) { return ""; }
            const Cleaned: string = String(Raw).replace(kPhoneNumberCore.SEPARATORS, "");
            if (Cleaned.length === 0) { return ""; }
            // "+46..." — already international. This is the WhatsApp case and the common vCard case.
            if (Cleaned.charAt(0) === "+") {
                return kPhoneNumberCore.FromDigits(Cleaned.substring(1));
            }
            // "0046..." — the international prefix written the old way. Same number, different hat.
            if (Cleaned.substring(0, 2) === "00") {
                return kPhoneNumberCore.FromDigits(Cleaned.substring(2));
            }
            // "070..." — national trunk form. Resolvable ONLY against a stated country.
            if (Cleaned.charAt(0) === "0") {
                if (DefaultCountryCode.length === 0) { return ""; }
                return kPhoneNumberCore.FromDigits(DefaultCountryCode + Cleaned.substring(1));
            }
            return kPhoneNumberCore.FromBareDigits(Cleaned, DefaultCountryCode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.ToE164");
            return "";
        }
    };

    /**
     * A bare digit string with no prefix at all ("636296080", "46701234567").
     *
     * THIS IS THE AMBIGUOUS CASE, and WITH NO COUNTRY STATED IT IS REFUSED.
     *
     * Measured 2026-09-10 against a real Google Contacts export, which is how the earlier, laxer
     * rule was caught: a Spanish mobile written the way Spain writes it, "636 29 60 80", has no
     * trunk prefix and no plus. Treating a bare string as already-international turned it into
     * "+636296080" - a valid-looking Philippine number belonging to somebody else entirely. That
     * is precisely the wrong join this class exists to prevent, and nothing downstream can detect
     * it: the key is well-formed, it simply names the wrong person.
     *
     * A subscriber number and a full international number are the same shape, so no rule over the
     * digits alone can separate them. With a country stated there IS a rule (below). Without one,
     * "" is the only honest answer - and a caller who KNOWS its digits are international, such as
     * one holding a WhatsApp JID's user part, says so by calling FromInternationalDigits.
     */
    private static FromBareDigits = (Digits: string, DefaultCountryCode: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (DefaultCountryCode.length === 0) { return ""; }
            // Already carries the country code - prepending it again would produce "+4646701...",
            // a number belonging to nobody, which then joins to nothing.
            if (Digits.substring(0, DefaultCountryCode.length) === DefaultCountryCode) {
                return kPhoneNumberCore.FromDigits(Digits);
            }
            if (Digits.length >= 11) { return kPhoneNumberCore.FromDigits(Digits); }
            return kPhoneNumberCore.FromDigits(DefaultCountryCode + Digits);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.FromBareDigits");
            return "";
        }
    };

    /**
     * Validate digits the CALLER GUARANTEES are already international, and add the plus.
     *
     * The escape hatch for sources that carry no ambiguity: a WhatsApp JID's user part, a vCard
     * `waid=` parameter, a Cloud API `wa_id`. All three are E.164 minus the plus by definition, so
     * the guessing ToE164 refuses to do is not needed - and routing them through ToE164 instead
     * would make them fail, because ToE164 cannot tell them from a national number.
     */
    public static FromInternationalDigits = (Digits: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Digits === null || Digits === undefined) { return ""; }
            return kPhoneNumberCore.FromDigits(String(Digits).replace(kPhoneNumberCore.SEPARATORS, ""));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.FromInternationalDigits");
            return "";
        }
    };

    /** Validate a pure digit string and put the plus back on. "" when it is not E.164-shaped. */
    private static FromDigits = (Digits: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (/^[0-9]+$/.test(Digits) === false) { return ""; }
            if (Digits.length < kPhoneNumberCore.MIN_DIGITS) { return ""; }
            if (Digits.length > kPhoneNumberCore.MAX_DIGITS) { return ""; }
            // A leading zero cannot survive into E.164 — no country code starts with one, so this
            // is a national number that reached here without its trunk prefix being understood.
            if (Digits.charAt(0) === "0") { return ""; }
            return "+" + Digits;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.FromDigits");
            return "";
        }
    };

    /** True when `Value` is already exactly E.164. Does not normalise; use ToE164 for that. */
    public static IsE164 = (Value: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Value === null || Value === undefined) { return false; }
            return /^\+[1-9][0-9]{6,14}$/.test(String(Value));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.IsE164");
            return false;
        }
    };

    /**
     * The E.164 form of a WhatsApp JID's user part ("46701234567@s.whatsapp.net" → "+46701234567").
     *
     * Returns "" for a LID ("...@lid"). A LID is a privacy-preserving surrogate, NOT a phone
     * number, and the digits in it are not dialable — treating one as a number would invent a
     * contact. Resolving a LID to its number is the caller's job, via WhatsApp's own mapping.
     */
    public static FromWhatsAppJid = (Jid: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Jid === null || Jid === undefined) { return ""; }
            const Text: string = String(Jid);
            const At: number = Text.indexOf("@");
            if (At < 0) { return kPhoneNumberCore.FromDigits(Text); }
            const Server: string = Text.substring(At + 1);
            if (Server !== "s.whatsapp.net" && Server !== "c.us") { return ""; }
            const User: string = Text.substring(0, At).split(":")[0];
            return kPhoneNumberCore.FromDigits(User);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kPhoneNumberCore.FromWhatsAppJid");
            return "";
        }
    };
}
